import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub, TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { type GitHubDiscussionDetail, parseGitHubDiscussionPayload } from "./githubDiscussionSchema";
import { safeGitHubUrl } from "./githubRenderUtils";

interface GitHubDiscussionRendererProps {
  block: StreamingBlock;
}

export const GitHubDiscussionRenderer = (props: GitHubDiscussionRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubDiscussionDetail | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubDiscussionPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_discussion_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="border-b border-primary-border px-3 py-2">
              <div class="flex items-baseline gap-2">
                <TbBrandGithub class="size-4 shrink-0 self-center text-primary-text" />
                <span class="shrink-0 font-mono text-xs text-muted-text">{data().reference}</span>
                <span class="truncate text-sm font-medium text-primary-text">{data().title}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">{formatIsoTimestamp(data().updatedAt)}</span>
              </div>
              <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                <Show when={data().answered}>
                  <span class="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {rt.t("github_discussions_card.answered")}
                  </span>
                </Show>
                <Show when={data().category}>{(category) => <span class="truncate">{category()}</span>}</Show>
                <Show when={data().author}>{(author) => <span class="truncate">{author()}</span>}</Show>
              </div>
            </div>

            <Show when={data().body}>
              {(body) => (
                <div class="px-3 py-2">
                  <ScrollableFadeContainer maxHeight="200px">
                    <CompleteMarkdown markdown={body()} />
                  </ScrollableFadeContainer>
                </div>
              )}
            </Show>

            <Show when={data().comments.length > 0}>
              <ul class="divide-y divide-primary-border border-t border-primary-border">
                <For each={data().comments}>
                  {(comment) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <span class="truncate text-sm font-medium text-primary-text">{comment.author}</span>
                        <span class="shrink-0 text-xs text-muted-text">{formatIsoTimestamp(comment.createdAt)}</span>
                      </div>
                      <div class="mt-1 line-clamp-3">
                        <CompleteMarkdown markdown={comment.body} />
                      </div>
                      <Show when={safeGitHubUrl(comment.url)}>
                        {(href) => (
                          <a
                            href={href()}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="mt-1 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                          >
                            {rt.t("github_comments_card.open_link")}
                            <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                          </a>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </Show>

            <div class="flex items-center gap-3 border-t border-primary-border px-3 py-2 text-xs text-muted-text">
              <Show when={safeGitHubUrl(data().url)}>
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-brand hover:underline"
                  >
                    {rt.t("github_discussion_card.open_link")}
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                )}
              </Show>
              <Show when={data().truncated}>
                <span>{rt.t("github_discussion_card.truncated")}</span>
              </Show>
            </div>
          </div>
        )}
      </Show>
    </Show>
  );
};
